import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldAlert, CheckCircle2, X, Building2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Aug 2026 · MedTech / Diagnostic asset review queue. Detects standalone
// medical devices, wearables, IVD kits, imaging systems from the biotech
// asset pool. Every detection needs an explicit ✅ Approve before removal;
// rejections are recorded so the same row never resurfaces. NDDS records
// are carved out — devices with a drug-delivery signal go into the
// ambiguous list, never auto-dropped.
export default function MedTechReviewPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/medtech-review/scan`, { withCredentials: true });
      setReport(data);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load MedTech review queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rowKey = (r) => `${r.biotech_id}::${r.asset_index}`;

  const decide = async (row, decision) => {
    const key = rowKey(row);
    setBusyIds((prev) => new Set(prev).add(key));
    try {
      const { data } = await axios.post(`${API}/medtech-review/decide`, {
        biotech_id: row.biotech_id,
        asset_index: row.asset_index,
        decision,
        asset_name: row.asset_name,
        reason: decision === 'approve' ? 'Approved via MedTech Review Queue' : 'Marked as false positive (drug asset — keep)',
      }, { withCredentials: true });

      if (decision === 'approve') {
        if (data.action === 'whole_company_removed') {
          toast.success(`Removed ${row.asset_name} (last therapeutic asset — whole company ${row.biotech_name} archived)`, { duration: 6000 });
        } else {
          toast.success(`Removed ${row.asset_name} from ${row.biotech_name} (${data.surviving_assets || 0} therapeutic asset(s) remain)`, { duration: 5000 });
        }
      } else if (decision === 'reject') {
        toast.success(`Marked as drug asset — kept. Will not re-appear in queue.`);
      } else {
        toast.info('Skipped for now.');
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Decision failed`);
    } finally {
      setBusyIds((prev) => {
        const n = new Set(prev);
        n.delete(key);
        return n;
      });
    }
  };

  const bulkApprove = async () => {
    if (selectedIds.size === 0) {
      toast.warning('Select rows to bulk-approve first');
      return;
    }
    setLoading(true);
    try {
      const allRows = [...(report?.confident_hits || []), ...(report?.ambiguous_review || [])];
      const items = allRows
        .filter((r) => selectedIds.has(rowKey(r)))
        .map((r) => ({ biotech_id: r.biotech_id, asset_index: r.asset_index, asset_name: r.asset_name }));
      const { data } = await axios.post(`${API}/medtech-review/bulk-approve`,
        { items, reason: 'Bulk approval from MedTech Review Queue' },
        { withCredentials: true },
      );
      const ok = data.results.filter((r) => r.ok).length;
      toast.success(`Approved ${ok}/${data.processed} — see results`, { duration: 6000 });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk approve failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (key) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const selectAllConfident = () => {
    const keys = (report?.confident_hits || []).map(rowKey);
    setSelectedIds(new Set(keys));
  };

  if (loading && !report) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const confident = report?.confident_hits || [];
  const ambiguous = report?.ambiguous_review || [];

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6" data-testid="medtech-review-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <ShieldAlert className="w-6 h-6 text-rose-600" /> MedTech / Diagnostic Review Queue
        </h1>
        <p className="text-sm text-slate-500 mt-1.5">
          ForesightRx tracks pharma &amp; biotech therapeutic assets only, including NDDS (New Drug Delivery Systems).
          Standalone medical devices, wearables, IVD kits, imaging systems, and pure diagnostic assays are out of scope.
          Every flagged asset needs your explicit approval before removal — archives are reversible.
        </p>
      </div>

      {report && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="border-slate-100"><CardContent className="p-4">
            <p className="text-2xl font-bold text-slate-900" data-testid="scanned-count">{report.total_assets_scanned}</p>
            <p className="text-xs text-slate-500">Assets scanned</p>
          </CardContent></Card>
          <Card className="border-slate-100"><CardContent className="p-4">
            <p className="text-2xl font-bold text-rose-600" data-testid="confident-count">{confident.length}</p>
            <p className="text-xs text-slate-500">Confident MedTech hits</p>
          </CardContent></Card>
          <Card className="border-slate-100"><CardContent className="p-4">
            <p className="text-2xl font-bold text-amber-600" data-testid="ambiguous-count">{ambiguous.length}</p>
            <p className="text-xs text-slate-500">Ambiguous — needs review</p>
          </CardContent></Card>
          <Card className="border-slate-100"><CardContent className="p-4">
            <p className="text-2xl font-bold text-slate-900">{report.total_flagged_companies}</p>
            <p className="text-xs text-slate-500">Companies affected</p>
          </CardContent></Card>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={load} className="text-xs" data-testid="refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={selectAllConfident} className="text-xs" data-testid="select-all-confident-btn">
          Select all {confident.length} confident hits
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              disabled={selectedIds.size === 0}
              className="text-xs bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="bulk-approve-btn"
            >
              Approve {selectedIds.size} selected
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Approve removal of {selectedIds.size} MedTech asset(s)?</AlertDialogTitle>
              <AlertDialogDescription>
                Every selected asset will be archived (reversible) and removed from live collections.
                Where an asset is the last therapeutic entry on its company, the whole company will be archived + removed too.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={bulkApprove} className="bg-rose-600 hover:bg-rose-700">Approve &amp; remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {[
        { title: 'Confident MedTech hits', rows: confident, tint: 'rose', testPrefix: 'confident' },
        { title: 'Ambiguous — needs your call', rows: ambiguous, tint: 'amber', testPrefix: 'ambiguous' },
      ].map(({ title, rows, tint, testPrefix }) => (
        <div key={title} data-testid={`section-${testPrefix}`}>
          <h2 className={`text-sm font-semibold text-${tint}-700 uppercase tracking-wider mb-2 flex items-center gap-2`}>
            {tint === 'rose' ? <ShieldAlert className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {title} ({rows.length})
          </h2>
          {rows.length === 0 ? (
            <p className="text-xs text-slate-400 italic px-4 py-3">— none —</p>
          ) : (
            <div className="border border-slate-100 rounded-lg divide-y divide-slate-100 bg-white">
              {rows.map((r) => {
                const key = rowKey(r);
                const busy = busyIds.has(key);
                return (
                  <div key={key} className="p-3 flex items-start gap-3 hover:bg-slate-50" data-testid={`${testPrefix}-row-${r.asset_name?.replace(/\s+/g, '-').toLowerCase()}`}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(key)}
                      onChange={() => toggleSelect(key)}
                      className="mt-1 rounded border-slate-300"
                      data-testid={`select-${key}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-800">{r.asset_name}</span>
                        {r.modality && <Badge variant="secondary" className="text-[10px]">{r.modality}</Badge>}
                        {r.stage && <Badge variant="secondary" className="text-[10px]">{r.stage}</Badge>}
                        {r.ndds_carveout && <Badge className="text-[10px] bg-blue-100 text-blue-800 border border-blue-300">NDDS carve-out</Badge>}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 text-[11px] text-slate-500">
                        <Building2 className="w-3 h-3" />
                        <a
                          href={`/biotech/${r.biotech_id}`}
                          className="hover:text-indigo-600 hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {r.biotech_name}
                        </a>
                        <span>·</span>
                        <span>{r.therapeutic_siblings} therapeutic sibling{r.therapeutic_siblings === 1 ? '' : 's'}</span>
                        <span>·</span>
                        <span className={r.recommendation === 'delete_whole_company' ? 'text-rose-700 font-semibold' : ''}>
                          {r.recommendation === 'delete_whole_company' ? 'whole company will be removed' : 'asset-only removal'}
                        </span>
                      </div>
                      <div className="mt-1.5 text-[11px] text-slate-600 italic">
                        {r.reason || `Markers: ${(r.fired_markers || []).slice(0, 5).join(', ')}`}
                      </div>
                      {r.description_excerpt && (
                        <div className="mt-1 text-[11px] text-slate-500 line-clamp-2">{r.description_excerpt}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => decide(r, 'approve')}
                        disabled={busy}
                        className="h-7 text-[11px] text-rose-700 border-rose-300 hover:bg-rose-50"
                        data-testid={`approve-btn-${key}`}
                      >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                        Approve
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => decide(r, 'reject')}
                        disabled={busy}
                        className="h-7 text-[11px] text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        data-testid={`reject-btn-${key}`}
                        title="This is actually a drug asset — keep it and never surface again"
                      >
                        <X className="w-3 h-3 mr-1" /> Keep (drug)
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
